import React, { useEffect } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { styled, IconButton } from "@mui/material";
import { useRouter } from "next/router";
import {
  KeyboardArrowDownRounded,
  KeyboardArrowRightRounded,
  DashboardRounded,
  InfoRounded,
  CalendarTodayRounded,
  ArticleRounded,
  ContactMailRounded,
  ShareRounded,
  EventRounded,
  HandymanRounded,
  FavoriteRounded,
  EmailRounded,
  VolunteerActivismRounded,
  PeopleRounded,
  LocationOnRounded,
  SettingsRounded,
} from "@mui/icons-material";
import Image from "next/image";
import { getCurrentUser } from "@/lib/auth.util";
import { useTranslation } from "next-i18next";

export interface NavigationItemProps {
  href?: string;
  text: string;
  icon?: React.ReactNode;
  open?: boolean;
  setOpen?: () => void;
  subItems?: NavigationItemProps[];
}

interface SideNavContentProps {
  open: boolean;
  navigationItems?: NavigationItemProps[];
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDrawer?: () => void;
  isDrawerOpen?: boolean;
  isMobile?: boolean;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const SideNavContent: React.FC<SideNavContentProps> = ({
  open,
  setOpen,
  toggleDrawer,
  isDrawerOpen,
  isMobile,
}) => {
  const { t } = useTranslation("common");
  const currentUser = getCurrentUser();
  const router = useRouter();

  const [openSublist, setOpenSublist] = React.useState<Record<string, boolean>>(
    {}
  );

  const handleRouteQuery = (page: string) => {
    if (router) router.push(page);
  };

  useEffect(() => {
    const updatedOpenSublist = { ...openSublist };
    navigationItems.forEach((item, index) => {
      updatedOpenSublist[index] = open && updatedOpenSublist[index];
    });
    setOpenSublist(updatedOpenSublist);
  }, [open]);

  const handleSublistToggle = (index: number) => {
    setOpen && setOpen(true);
    setOpenSublist((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[index] = !prevState[index];
      return newState;
    });
  };

  const handleNavigationItemOnClick = (
    navItem: NavigationItemProps,
    index: number
  ) => {
    if (navItem.href) {
      handleRouteQuery(navItem.href);
    } else {
      handleSublistToggle(index);
    }
  };

  const navigationItems: NavigationItemProps[] = [
    {
      text: t("dashboard.nav.home"),
      href: "/dashboard",
      icon: <DashboardRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.aboutPage.title"),
      href: "/dashboard/about",
      icon: <InfoRounded />,
      subItems: [],
    },

    {
      text: t("dashboard.blogsPage.title"),
      href: "/dashboard/blogs",
      icon: <ArticleRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.contactsPage.title"),
      href: "/dashboard/contacts",
      icon: <ContactMailRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.contactPage.title"),
      href: "/dashboard/contact",
      icon: <EmailRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.socialLinksPage.title"),
      href: "/dashboard/social-links",
      icon: <ShareRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.eventsPage.title"),
      href: "/dashboard/events",
      icon: <EventRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.services.title"),
      href: "/dashboard/services",
      icon: <HandymanRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.donatePage.title"),
      href: "/dashboard/donate",
      icon: <FavoriteRounded />,
      subItems: [],
    },

    {
      text: t("dashboard.volunteerPage.title"),
      href: "/dashboard/volunteer",
      icon: <VolunteerActivismRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.volunteersPage.title"),
      href: "/dashboard/volunteers",
      icon: <PeopleRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.governorates.title") || "Governorates",
      href: "/dashboard/governorates",
      icon: <LocationOnRounded />,
      subItems: [],
    },
    {
      text: t("dashboard.metaPage.title") || "Meta Tags",
      href: "/dashboard/meta",
      icon: <SettingsRounded />,
      subItems: [],
    },
  ];
  return (
    <div className="h-full relative flex flex-col [&_.MuiList-root]:!max-h-full  w-full bg-primary border-r-[0.2rem] border-secondary overflow-hidden">
      <DrawerHeader
        className={`flex items-center !border-none px-[1rem] min-h-[7rem] ${
          isMobile ? "justify-center" : "justify-between"
        }`}
      >
        <div
          onClick={() => router.push("/dashboard")}
          className={`cursor-pointer flex items-center justify-center  flex-1 ${
            open ? "w-[12rem] h-[12rem]" : "hidden"
          }  my-4`}
        >
          <Image
            className={`transition-all filter brightness-0 invert object-contain duration-300 ease-in-out  rounded-full ${
              open ? "w-[12rem] h-[12rem]" : "hidden w-0"
            } `}
            src="/dashboard/icons/long-logo.png"
            alt="logo"
            width={320}
            height={320}
          />
        </div>

        {/* Drawer Toggle Button */}
        {toggleDrawer && (
          <IconButton
            onClick={toggleDrawer}
            className={`p-[0.5rem] hover:bg-gray-100 rounded-full ${
              isMobile ? "!hidden" : "block"
            }`}
          >
            <Image
              src="/icons/drawer-arrow.svg"
              alt="toggle drawer"
              width={20}
              height={20}
              className={`transition-transform duration-300 ${
                isDrawerOpen ? "rotate-180" : ""
              }`}
            />
          </IconButton>
        )}
      </DrawerHeader>

      {/* Navigation Items */}
      <List className="flex-1 overflow-y-auto overflow-x-hidden">
        {navigationItems.map((item, index) => {
          const isActive = (item as any).href
            ? router.pathname === (item as any).href ||
              (router.pathname.startsWith((item as any).href + "/") &&
                (item as any).href !== "/" &&
                (item as any).href !== "/dashboard") ||
              (item.subItems &&
                item.subItems.some((subItem: any) => {
                  if (!subItem.href) return false;
                  return (
                    router.pathname === subItem.href ||
                    router.pathname.startsWith(subItem.href + "/")
                  );
                }))
            : item.subItems &&
              item.subItems.some((subItem: any) => {
                if (!subItem.href) return false;
                return (
                  router.pathname === subItem.href ||
                  router.pathname.startsWith(subItem.href + "/")
                );
              });
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isSubListOpen = openSublist[index];

          return (
            <div key={index}>
              <ListItem
                disablePadding
                sx={{ display: "block" }}
                className={`${open ? "w-full" : "w-full"}`}
              >
                <ListItemButton
                  onClick={() => handleNavigationItemOnClick(item, index)}
                  className={`min-h-[3rem] transition-all duration-300 ease-in-out ${
                    open ? "px-[1rem]" : "px-[0.5rem]"
                  } ${
                    isActive
                      ? "bg-secondary text-white hover:bg-secondary"
                      : "hover:bg-tertiary"
                  }`}
                  sx={{
                    justifyContent: open ? "initial" : "center",
                    borderRadius: "0.5rem",
                    margin: "0.25rem 0.5rem",
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: isActive ? "white" : "white",
                      transition: "margin-right 0.3s ease-in-out",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: open ? 1 : 0,
                      color: isActive ? "white" : "white",
                      transition: "opacity 0.3s ease-in-out",
                    }}
                  />
                  {hasSubItems && open && (
                    <div className="ml-auto">
                      {isSubListOpen ? (
                        <KeyboardArrowDownRounded />
                      ) : (
                        <KeyboardArrowRightRounded />
                      )}
                    </div>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Sub Items */}
              {hasSubItems && (
                <Collapse in={isSubListOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems?.map((subItem, subIndex) => {
                      const isSubActive =
                        subItem.href &&
                        (router.pathname === subItem.href ||
                          router.pathname.startsWith(subItem.href + "/"));
                      return (
                        <ListItemButton
                          key={subIndex}
                          onClick={() =>
                            subItem.href && handleRouteQuery(subItem.href)
                          }
                          className={`min-h-[2.5rem] ml-[1rem] ${
                            isSubActive
                              ? "bg-secondary text-white hover:bg-secondary"
                              : "hover:bg-gray-100"
                          }`}
                          sx={{
                            borderRadius: "0.5rem",
                            margin: "0.125rem 0.5rem",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 2,
                              justifyContent: "center",
                              color: isSubActive ? "white" : "inherit",
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.text}
                            sx={{
                              color: isSubActive ? "white" : "inherit",
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    </div>
  );
};

export default SideNavContent;
